import {
  markBackendAvailable,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { I18n } from '@affine/i18n';

import { localAuthorityRequestHeaders } from '../../local-authority';
import {
  BackendRequestGate,
  defaultBackendRequestGate,
  gatedBackendFetch,
  isExplicitBackendRequestCancellation,
} from './backend-request-gate';
import {
  defaultRequestCoordinator,
  type RequestCoordinatorPort,
  type WorkspaceRequestCategory,
  type WorkspaceRequestMode,
  type WorkspaceRequestPriority,
} from './request-coordinator';
import {
  defaultScopedDegradationStore,
  sanitizeUserFacingError,
  ScopedDegradationStore,
  scopeForRequestCategory,
  shouldSuppressGlobalToast,
} from './scoped-degradation-store';

declare const BUILD_CONFIG: { appVersion?: string } | undefined;
let mutationRequestSequence = 0;

export interface WorkspaceApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof globalThis.fetch;
  coordinator?: RequestCoordinatorPort;
  degradationStore?: ScopedDegradationStore;
  requestGate?: BackendRequestGate;
}

export interface WorkspaceApiRequest {
  workspaceId: string;
  baseUrl?: string;
  path: string;
  init?: RequestInit;
  category?: WorkspaceRequestCategory;
  key?: string;
  priority?: WorkspaceRequestPriority;
  mode?: WorkspaceRequestMode;
}

export interface WorkspaceApiGraphQLRequest {
  workspaceId: string;
  baseUrl?: string;
  path?: string;
  query: string;
  variables?: Record<string, unknown>;
  operation: 'query' | 'mutation';
  category?: WorkspaceRequestCategory;
  priority?: WorkspaceRequestPriority;
  mode?: WorkspaceRequestMode;
  signal?: AbortSignal;
}

export type WorkspaceApiFetchPolicy = Pick<
  WorkspaceApiRequest,
  'workspaceId' | 'category' | 'key' | 'priority' | 'mode'
>;

export class WorkspaceApiError extends Error {
  readonly skipGlobalSWRToast: boolean;

  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly responseBody: unknown,
    options: { skipGlobalSWRToast: boolean }
  ) {
    super(message);
    this.name = code;
    this.skipGlobalSWRToast = options.skipGlobalSWRToast;
  }
}

export class WorkspaceApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof globalThis.fetch;
  private readonly coordinator: RequestCoordinatorPort;
  private readonly degradationStore: ScopedDegradationStore;
  private readonly requestGate: BackendRequestGate;

  constructor(options: WorkspaceApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? defaultBaseUrl();
    this.fetchImpl =
      options.fetchImpl ??
      (((input, init) =>
        globalThis.fetch(input, init)) as typeof globalThis.fetch);
    this.coordinator = options.coordinator ?? defaultRequestCoordinator;
    this.degradationStore =
      options.degradationStore ?? defaultScopedDegradationStore;
    this.requestGate = options.requestGate ?? new BackendRequestGate();
  }

  async request(input: WorkspaceApiRequest): Promise<Response> {
    const category = input.category ?? 'workspace-api';
    const mode = input.mode ?? 'background';
    const priority =
      input.priority ?? (mode === 'foreground' ? 'foreground' : 'visible');
    const method = (input.init?.method ?? 'GET').toUpperCase();
    const key = input.key ?? defaultCoordinatorKey(method, input.path);

    const response = await this.coordinator.execute({
      workspaceId: input.workspaceId,
      category,
      key,
      priority,
      mode,
      signal: input.init?.signal ?? undefined,
      run: signal => this.performRequest(input, signal),
    });

    // GET requests are deduplicated by RequestCoordinator. A Response body is
    // single-use, so returning the shared instance lets the first consumer
    // exhaust it and makes concurrent consumers fail with "body stream already
    // read". Keep the coordinated response untouched and give every caller an
    // independently readable branch of its body stream.
    return response.clone();
  }

  async requestJson<T>(input: WorkspaceApiRequest): Promise<T> {
    const category = input.category ?? 'workspace-api';
    const mode = input.mode ?? 'background';
    const priority =
      input.priority ?? (mode === 'foreground' ? 'foreground' : 'visible');
    const method = (input.init?.method ?? 'GET').toUpperCase();
    const key = `${input.key ?? defaultCoordinatorKey(method, input.path)}:json`;

    return this.coordinator.execute({
      workspaceId: input.workspaceId,
      category,
      key,
      priority,
      mode,
      signal: input.init?.signal ?? undefined,
      run: async signal => {
        const response = await this.performRequest(input, signal);
        return (await response.json()) as T;
      },
    });
  }

  async requestGraphQL<T>(input: WorkspaceApiGraphQLRequest): Promise<T> {
    const body = JSON.stringify({
      query: input.query,
      variables: input.variables,
    });

    return this.requestJson<T>({
      workspaceId: input.workspaceId,
      baseUrl: input.baseUrl,
      path: input.path ?? '/graphql',
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: input.signal,
      },
      category: input.category,
      key:
        input.operation === 'query'
          ? `graphql:${hashRequest(body)}`
          : undefined,
      priority: input.priority,
      mode: input.mode,
    });
  }

  /**
   * Central transport/error boundary for a callback that already runs inside
   * RequestCoordinator.execute/executeBatch. It deliberately skips a second
   * scheduling pass while preserving degradation and response handling.
   */
  async requestJsonWithinCoordinator<T>(
    input: WorkspaceApiRequest,
    signal: AbortSignal
  ): Promise<T> {
    const response = await this.performRequest(input, signal);
    return (await response.json()) as T;
  }

  private async performRequest(
    input: WorkspaceApiRequest,
    signal: AbortSignal
  ): Promise<Response> {
    const category = input.category ?? 'workspace-api';
    const mode = input.mode ?? 'background';
    const method = (input.init?.method ?? 'GET').toUpperCase();
    const sourceId = `${method}:${input.path}`;
    const url = toUrl(input.path, input.baseUrl ?? this.baseUrl);
    let response: Response;
    try {
      response = await gatedBackendFetch(
        url,
        {
          credentials: 'include',
          ...input.init,
          signal,
          headers: {
            ...streamlainVersionHeader(),
            ...localAuthorityRequestHeaders(url),
            ...input.init?.headers,
          },
        },
        {
          fetchImpl: this.fetchImpl,
          gate: this.requestGate,
          isCancellation: error =>
            isExplicitBackendRequestCancellation(error, signal),
        }
      );
    } catch (error) {
      if (signal.aborted) {
        throw signal.reason;
      }
      this.markNetworkError(input.workspaceId, category, mode, sourceId, error);
    }

    if (!response.ok) {
      await this.handleError(
        response,
        input.workspaceId,
        category,
        mode,
        sourceId
      );
    }

    this.degradationStore.markAvailable(
      {
        scope: scopeForRequestCategory(category),
        workspaceId: input.workspaceId,
      },
      sourceId
    );
    markBackendAvailable();
    return response;
  }

  private async handleError(
    response: Response,
    workspaceId: string,
    category: WorkspaceRequestCategory,
    mode: WorkspaceRequestMode,
    sourceId: string
  ): Promise<never> {
    const body = await readResponseBody(response);
    const degradation = readBackendDegradation(body);
    const code = readErrorCode(body) ?? String(response.status);
    const message = degradation
      ? messageForBackendDegradation(code, response.status, category)
      : (sanitizeUserFacingError(readErrorMessage(body)) ??
        defaultMessageForStatus(response.status, category));
    const error = new WorkspaceApiError(response.status, code, message, body, {
      skipGlobalSWRToast: shouldSuppressGlobalToast(
        { status: response.status, code },
        { mode, category }
      ),
    });

    if (degradation || response.status >= 500 || response.status === 429) {
      this.coordinator.markDegraded(workspaceId, category, {
        status: response.status,
        headers: response.headers,
      });
      this.degradationStore.markDegraded({
        key: {
          scope: scopeForRequestCategory(category),
          workspaceId,
        },
        sourceId,
        code,
        message,
        requestId: degradation?.requestId,
        retryable: degradation?.retryable ?? response.status >= 500,
        severity: degradation?.userVisibleSeverity,
      });
    }
    if (response.status >= 500) {
      markBackendUnavailable(error);
    }

    throw error;
  }

  private markNetworkError(
    workspaceId: string,
    category: WorkspaceRequestCategory,
    mode: WorkspaceRequestMode,
    sourceId: string,
    cause: unknown
  ): never {
    const error = new WorkspaceApiError(
      504,
      'NETWORK_ERROR',
      I18n['com.streamlain.error.workspace-unavailable.message'](),
      cause,
      {
        skipGlobalSWRToast: shouldSuppressGlobalToast(
          { status: 504, code: 'NETWORK_ERROR' },
          { mode, category }
        ),
      }
    );
    this.degradationStore.markDegraded({
      key: { scope: scopeForRequestCategory(category), workspaceId },
      sourceId,
      code: 'NETWORK_ERROR',
      message: error.message,
      retryable: true,
      severity: mode === 'foreground' ? 'blocking' : 'inline',
    });
    markBackendUnavailable(error);
    throw error;
  }
}

export const defaultWorkspaceApiClient = new WorkspaceApiClient({
  requestGate: defaultBackendRequestGate,
});

/**
 * Fetch-compatible boundary for workspace-scoped transports that need a raw
 * Response (uploads, GraphQL, downloads). Workspace scope must be explicit or
 * derivable from the URL/body; an unscoped internal request fails before I/O.
 */
export function workspaceApiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  policy: Partial<WorkspaceApiFetchPolicy> = {}
): Promise<Response> {
  const workspaceId =
    policy.workspaceId ?? inferWorkspaceIdFromRequest(input, init);
  if (!workspaceId) {
    throw new Error('Workspace API request requires an explicit workspaceId');
  }
  return defaultWorkspaceApiClient.request({
    workspaceId,
    path: requestPath(input),
    init,
    category: policy.category,
    key: policy.key,
    priority: policy.priority,
    mode: policy.mode,
  });
}

function defaultBaseUrl(): string {
  return typeof location === 'undefined' ? 'http://localhost' : location.origin;
}

function toUrl(path: string, baseUrl: string): string {
  return new URL(path, baseUrl).href;
}

function requestPath(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function inferWorkspaceIdFromRequest(
  input: RequestInfo | URL,
  init?: RequestInit
): string | null {
  const path = requestPath(input);
  const routeMatch = path.match(/\/api\/workspaces?\/([^/?#]+)/i);
  if (routeMatch?.[1]) {
    return decodeURIComponent(routeMatch[1]);
  }
  if (typeof init?.body !== 'string') return null;
  try {
    const parsed = JSON.parse(init.body) as {
      variables?: { workspaceId?: unknown };
      workspaceId?: unknown;
    };
    const workspaceId = parsed.variables?.workspaceId ?? parsed.workspaceId;
    return typeof workspaceId === 'string' && workspaceId.trim()
      ? workspaceId
      : null;
  } catch {
    return null;
  }
}

function streamlainVersionHeader(): Record<string, string> {
  const version =
    typeof BUILD_CONFIG === 'undefined' ? undefined : BUILD_CONFIG.appVersion;
  return version ? { 'x-streamlain-version': version } : {};
}

async function readResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.startsWith('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  try {
    return await response.text();
  } catch {
    return null;
  }
}

function readBackendDegradation(body: unknown):
  | {
      requestId?: string;
      retryable?: boolean;
      userVisibleSeverity?: 'silent' | 'inline' | 'blocking';
    }
  | undefined {
  const record = body as Record<string, unknown> | undefined;
  const error = record?.error as Record<string, unknown> | undefined;
  const rest = error?.backendDegradation as Record<string, unknown> | undefined;
  const graphQLError = readFirstGraphQLError(record);
  const extensions = graphQLError?.extensions as
    | Record<string, unknown>
    | undefined;
  const graphql = extensions?.backendDegradation as
    | Record<string, unknown>
    | undefined;
  const value = rest ?? graphql;
  return value && typeof value === 'object' ? value : undefined;
}

function readErrorCode(body: unknown): string | undefined {
  const record = body as Record<string, unknown> | undefined;
  const error = record?.error as Record<string, unknown> | undefined;
  const graphQLError = readFirstGraphQLError(record);
  const extensions = graphQLError?.extensions as
    | Record<string, unknown>
    | undefined;
  const code = error?.code ?? extensions?.code;
  return typeof code === 'string' ? code : undefined;
}

function readErrorMessage(body: unknown): string | undefined {
  if (typeof body === 'string') return body;
  const record = body as Record<string, unknown> | undefined;
  const error = record?.error as Record<string, unknown> | undefined;
  const graphQLError = readFirstGraphQLError(record);
  const message = error?.message ?? graphQLError?.message;
  return typeof message === 'string' ? message : undefined;
}

function readFirstGraphQLError(
  body: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  const errors = body?.errors;
  if (!Array.isArray(errors)) return undefined;
  return errors.find(
    (value): value is Record<string, unknown> =>
      value !== null && typeof value === 'object'
  );
}

function messageForBackendDegradation(
  code: string,
  status: number,
  category: WorkspaceRequestCategory
): string {
  if (code === 'TENANT_DATABASE_MIGRATION_IN_PROGRESS') {
    return I18n['com.streamlain.error.workspace-preparing.message']();
  }
  if (code === 'TENANT_NOT_FOUND' || code === 'WORKSPACE_NOT_FOUND') {
    return I18n['com.streamlain.error.workspace-not-found.message']();
  }
  return defaultMessageForStatus(status, category);
}

function defaultMessageForStatus(
  status: number,
  category: WorkspaceRequestCategory
): string {
  if (status === 429) {
    return category === 'foreground-action'
      ? I18n['com.streamlain.error.too-many-requests.action-message']()
      : I18n['com.streamlain.error.too-many-requests.message']();
  }
  return I18n['com.streamlain.error.workspace-unavailable.message']();
}

function defaultCoordinatorKey(method: string, path: string): string {
  const stableKey = `${method}:${path}`;
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') {
    return stableKey;
  }
  mutationRequestSequence += 1;
  return `${stableKey}:mutation:${mutationRequestSequence}`;
}

function hashRequest(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}
